import React, { useEffect, useRef, useState } from 'react'

import { fetchSandboxVariants, SandboxVariant } from 'api/sandboxVariants'
import { useLosVersion } from 'hooks/useLosVersion'

import {
    SelectorWrapper,
    SelectorPill,
    SelectorDropdown,
    SearchWrapper,
    SearchInput,
    DropdownItem,
} from './VersionSelector.styled'

export function VersionSelector(): JSX.Element | null {
    const { losVersion, setLosVersion } = useLosVersion()
    const [variants, setVariants] = useState<SandboxVariant[]>([])
    const [open, setOpen] = useState(false)
    const [filterText, setFilterText] = useState('')
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        fetchSandboxVariants().then(setVariants)
    }, [])

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false)
                setFilterText('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)

        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const activeLabel =
        losVersion === 'stable'
            ? 'LOS: Stable'
            : `LOS: ${losVersion.replace('pr-', 'PR #')}`

    const handleSelect = async (version: string) => {
        // Await setLosVersion so the session key is stored in localStorage
        // before the page reloads.  Without this, the key fetch races with
        // the reload and variant API calls on the next page load fire before
        // the key arrives, producing 401s.
        await setLosVersion(version)
        setOpen(false)
        setFilterText('')
        window.location.reload()
    }

    const handleToggle = () => {
        setOpen((o) => {
            if (o) setFilterText('')

            return !o
        })
    }

    const filteredVariants =
        filterText.trim() === ''
            ? variants
            : variants.filter(
                  (v) =>
                      v.label
                          .toLowerCase()
                          .includes(filterText.toLowerCase()) ||
                      String(v.prNumber).includes(filterText),
              )

    return (
        <SelectorWrapper ref={ref}>
            <SelectorPill
                onClick={handleToggle}
                $active={losVersion !== 'stable'}
                aria-expanded={open}
                aria-haspopup="listbox"
            >
                {activeLabel} ▾
            </SelectorPill>
            {open && (
                <SelectorDropdown role="listbox">
                    {variants.length > 5 && (
                        <SearchWrapper role="presentation">
                            <SearchInput
                                type="text"
                                placeholder="Search variants…"
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                            />
                        </SearchWrapper>
                    )}
                    <DropdownItem
                        role="option"
                        aria-selected={losVersion === 'stable'}
                        $selected={losVersion === 'stable'}
                        onClick={() => handleSelect('stable')}
                    >
                        ● Stable (default)
                    </DropdownItem>
                    {filteredVariants.map((v) => (
                        <DropdownItem
                            key={v.prNumber}
                            role="option"
                            aria-selected={losVersion === `pr-${v.prNumber}`}
                            $selected={losVersion === `pr-${v.prNumber}`}
                            onClick={() => handleSelect(`pr-${v.prNumber}`)}
                        >
                            {v.label}
                        </DropdownItem>
                    ))}
                </SelectorDropdown>
            )}
        </SelectorWrapper>
    )
}
